import type { User } from "@/types";
import { isPlatformAdmin } from "@/lib/roles";

/**
 * Free (Basic) buyer: holds the buyer role, no staff role, and no active tier
 * subscription. Free buyers browse the marketplace but member-only features
 * (advisor contact, exact locations, offers, favorites, coming-soon deals,
 * SBA calculator) show Upgrade CTAs that lead to /choose-plan.
 */
export function isFreeBuyerUser(user: User | null | undefined): boolean {
  if (!user) return false;
  const roles = user.roles ?? [];
  if (!roles.includes("buyer")) return false;
  if (roles.some((r) => r === "admin" || r === "org_admin" || r === "seller" || r === "bl_advisor")) return false;
  return !user.has_active_subscription;
}

/** True for staff (admin / seller / BL advisor). */
export function isStaffUser(user: User | null | undefined): boolean {
  const roles = user?.roles ?? [];
  return roles.some((r) => r === "admin" || r === "org_admin" || r === "seller" || r === "bl_advisor");
}

/**
 * Operator-tier access: the buyer is on the paid **Operator** plan, or the user
 * is staff. Gates Operator-only features (AI voice filter, Deal pipeline CRM, …)
 * that Basic + Explorer buyers don't get — see the compare table on /choose-plan.
 */
export function hasOperatorAccess(user: User | null | undefined): boolean {
  if (!user) return false;
  if (isStaffUser(user)) return true;
  return user.has_active_subscription && (user.plan_name ?? "").toLowerCase() === "operator";
}

/**
 * Where an "Upgrade to <tier>" CTA should send this user.
 *
 * The two paths are genuinely different flows, and picking the wrong one costs
 * the buyer money:
 *
 * * No subscription yet → /choose-plan, the first-purchase pricing page.
 * * Already subscribed → the Change Plan section of subscription settings,
 *   which calls /payments/upgrade-subscription and charges only the prorated
 *   difference on the saved card. Sending an existing subscriber to
 *   /choose-plan instead would run a SECOND Stripe subscription while the old
 *   one keeps billing — /payments/activate-subscription repoints the DB row but
 *   never cancels the live Stripe sub.
 *
 * `tierName` deep-links straight to that tier's upgrade dialog.
 * Callers wrap the result in orgScopedPath().
 */
export function upgradeCtaPath(user: User | null | undefined, tierName?: string): string {
  if (!user?.has_active_subscription) return "/choose-plan";
  const query = tierName ? `?upgrade=${encodeURIComponent(tierName)}` : "";
  return `/buyer/settings/subscription${query}`;
}

/**
 * Niva (the BL AI Deal Assistant) is an Operator feature that Explorer buyers get
 * FREE FOR THEIR FIRST WEEK. The window itself is computed on the server
 * (services/tier.niva_trial_ends_at) and arrives as user.niva_trial_ends_at, so
 * this file only compares it to the clock — the same value gates POST
 * /chatbot/message, and these helpers are the panel's friendly face of that rule,
 * never the rule itself.
 *
 * null = no trial applies (Basic, Operator, staff, lapsed subscription).
 */
export function nivaTrialEndsAt(user: User | null | undefined): Date | null {
  const raw = user?.niva_trial_ends_at;
  if (!raw) return null;
  const end = new Date(raw);
  return Number.isNaN(end.getTime()) ? null : end;
}

/** True while an Explorer buyer's free week with Niva is still running. */
export function isNivaTrialActive(user: User | null | undefined): boolean {
  const end = nivaTrialEndsAt(user);
  return end !== null && end.getTime() > Date.now();
}

/**
 * True once an Explorer buyer's free week has run out — distinct from "never had
 * one", so the upgrade wall can say "your free week ended" instead of pitching a
 * feature they have been using all week.
 */
export function isNivaTrialExpired(user: User | null | undefined): boolean {
  const end = nivaTrialEndsAt(user);
  return end !== null && end.getTime() <= Date.now();
}

/** Whole days left in the free week, rounded up (1 = "last day"), else null. */
export function nivaTrialDaysLeft(user: User | null | undefined): number | null {
  const end = nivaTrialEndsAt(user);
  if (end === null) return null;
  const ms = end.getTime() - Date.now();
  return ms > 0 ? Math.ceil(ms / 86_400_000) : null;
}

/** Who may actually talk to Niva: Operator, staff, or a live Explorer free week. */
export function canUseNiva(user: User | null | undefined): boolean {
  return hasOperatorAccess(user) || isNivaTrialActive(user);
}

/** The AI voice search filter is Operator-only. */
export function canUseVoiceSearch(user: User | null | undefined): boolean {
  return hasOperatorAccess(user);
}

/**
 * The single portal admin account — the same email the backend checks in
 * app/routers/admin.py's PORTAL_ADMIN_EMAIL. Used to gate Talk to Your Data
 * (Niva's NL-to-SQL mode) client-side; the backend re-checks this by email
 * AND role on every request, so this is purely a UI convenience, never the
 * actual boundary. Centralised here so it isn't re-typed as a string literal
 * (previously duplicated ad hoc in Profile.tsx / AdminUploadNda.tsx / AdminUsers.tsx).
 */
export const PORTAL_ADMIN_EMAIL = "admin@portal.com";

export function isPortalAdmin(user: User | null | undefined): boolean {
  return !!user && user.email === PORTAL_ADMIN_EMAIL && isPlatformAdmin(user);
}
